'use client'

import { useState, useEffect, useMemo, useCallback, useRef, useReducer } from 'react'
import { MoreVertical, PieChart, Calendar, BarChart as BarChartIcon, PieChart as PieChartIcon, X, ChevronDown, AreaChart as AreaChartIcon, Percent as PercentIcon, LineChart as LineChartIcon, Maximize, Minimize } from 'lucide-react'
import { fetchNewsClippings } from '@/utils/supabase-queries'
import { useRouter, useSearchParams } from 'next/navigation'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { throttleEffect, debounce } from '@/lib/performance'

// Import types
import { 
  ActivityDataPoint, 
  SentimentCount, 
  CategoryCount, 
  PeriodType, 
  CustomDateRange,
  BrandDataPoint
} from './types'

// Import utility functions
import { groupClippingsByPeriod } from './utils/dataProcessing'
import { getCurrentPeriod, getDateRangeForPeriod } from './utils/dateUtils'
import { 
  getSentimentColor, 
  getSentimentColorValue, 
  getYAxisLabels, 
  getCategoryColor 
} from './utils/styleUtils'
import { processClippingsData } from './utils/processData'

// Import components
import { SentimentPieChart } from './components/SentimentPieChart'
import { CategoryBarChart } from './components/CategoryBarChart'
import { ActivityBarChart } from './components/ActivityBarChart'
import { BrandPercentChart } from './components/BrandPercentChart'
import { DateRangePicker } from './components/DateRangePicker'
import { CombinedPieChart } from './components/CombinedPieChart'
import { PercentChart } from './components/PercentChart'
import { LineChart } from './components/LineChart'
import { ActivityGraphControls } from './components/ActivityGraphControls'
import { ChartRenderer } from './components/ChartRenderer'

// Add CSS at the top of the file with a class to center legends in mobile view
import './legend-center.css'

interface ActivityGraphProps {
  clippings?: any[];
  selectedPeriod?: PeriodType;
  onPeriodChange?: (period: PeriodType) => void;
  selectedBrand?: string;
  dashboardFilters?: boolean;
  showAllCategories?: boolean;
  activeDashboardFilters?: { [key: string]: string[] };
  dashboardMode?: boolean;
}

// Add a custom style for the legend to center it on mobile
const legendStyle = {
  '@media (max-width: 640px)': {
    display: 'flex !important',
    justifyContent: 'center !important',
    width: '100% !important',
    margin: '0 auto !important',
    flexWrap: 'wrap !important'
  }
};

export default function ActivityGraph({ 
  clippings: propClippings, 
  selectedPeriod = 'Weekly',
  onPeriodChange,
  selectedBrand,
  dashboardFilters = false,
  showAllCategories = false,
  activeDashboardFilters = {},
  dashboardMode = false,
}: ActivityGraphProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showExcludedCategories] = useLocalStorage('newsClipping_showExcludedCategories', false);
  
  // Define a reducer for all chart data state
  type ChartState = {
    activityData: ActivityDataPoint[];
    sentimentData: SentimentCount[];
    categoryData: CategoryCount[];
    brandData: BrandDataPoint[];
    brandCategories: CategoryCount[];
    sourceData: CategoryCount[];
    categoriesByPeriodData: { [period: string]: CategoryCount[] };
    brandsByPeriodData: { [period: string]: CategoryCount[] };
    sourcesByPeriodData: { [period: string]: CategoryCount[] };
    periodSentimentData: SentimentCount[];
    maxValue: number;
    totalCount: number;
    isLoading: boolean;
  };
  
  const initialState: ChartState = {
    activityData: [],
    sentimentData: [],
    categoryData: [],
    brandData: [],
    brandCategories: [],
    sourceData: [],
    categoriesByPeriodData: {},
    brandsByPeriodData: {},
    sourcesByPeriodData: {},
    periodSentimentData: [],
    maxValue: 10,
    totalCount: 0,
    isLoading: true
  };
  
  type ChartAction = 
    | { type: 'SET_DATA', payload: Partial<ChartState> }
    | { type: 'RESET' };
    
  function chartReducer(state: ChartState, action: ChartAction): ChartState {
    switch (action.type) {
      case 'SET_DATA':
        return { ...state, ...action.payload, isLoading: false };
      case 'RESET':
        return { ...initialState };
      default:
        return state;
    }
  }
  
  const [chartState, dispatch] = useReducer(chartReducer, initialState);
  
  // UI state
  const [sentimentTypes, setSentimentTypes] = useState<string[]>(['Positive', 'Negative', 'Neutral']);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 90)),
    endDate: new Date()
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [breakdownType, setBreakdownType] = useState<'sentiment' | 'category' | 'brand' | 'source'>('sentiment');
  const [activeView, setActiveView] = useState<'activity' | 'analysis'>('activity');
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState<'sentiment' | 'category' | 'brand' | 'source' | null>(null);
  const [selectedFilterValue, setSelectedFilterValue] = useState<string | null>(null);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [chartType, setChartType] = useState<'bar' | 'area' | 'percent' | 'line'>('area');
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Flag to prevent multiple data processing calls
  const hasProcessedRef = useRef(false);
  
  // Process data once
  useEffect(() => {
    // Skip if we've already processed the data or have no data
    if (hasProcessedRef.current || !propClippings || propClippings.length === 0) {
      if (!propClippings || propClippings.length === 0) {
        dispatch({ type: 'SET_DATA', payload: { isLoading: false } });
      }
      return;
    }
    
    // Process data
    const data = processClippingsData(
      propClippings,
      selectedPeriod,
      customDateRange,
      selectedBrand,
      showAllCategories,
      dashboardFilters,
      activeDashboardFilters
    );
    
    // Update state via reducer
    dispatch({ 
      type: 'SET_DATA', 
      payload: {
        activityData: data.activityData,
        sentimentData: data.sentimentData,
        categoryData: data.categoryData,
        brandData: data.brandData,
        brandCategories: data.brandCategoriesData,
        sourceData: data.sourceData,
        categoriesByPeriodData: data.categoriesByPeriod,
        brandsByPeriodData: data.brandsByPeriod,
        sourcesByPeriodData: data.sourcesByPeriod,
        periodSentimentData: data.sentimentData,
        maxValue: data.maxValue || 10,
        totalCount: data.totalCount
      }
    });
    
    // Mark as processed
    hasProcessedRef.current = true;
  }, [propClippings]);
  
  // Destructure state for use in the component
  const {
    activityData,
    sentimentData,
    categoryData,
    brandData,
    brandCategories,
    sourceData,
    categoriesByPeriodData,
    brandsByPeriodData,
    sourcesByPeriodData,
    periodSentimentData,
    maxValue,
    totalCount,
    isLoading
  } = chartState;

  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: new Date(value)
    }))
  }

  // Memoize the period change handler to prevent recreating on every render
  const handlePeriodChange = useCallback((newPeriod: PeriodType, e?: React.ChangeEvent<HTMLSelectElement>) => {
    // Prevent any default form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Immediately set local state to update the UI
    if (newPeriod !== selectedPeriod) {
      // This will trigger a rerender with the new selection
      // We store state locally first before telling the parent
      
      // Set appropriate date ranges based on the selected period
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let newStartDate = new Date(today);
      let newEndDate = new Date(today);
      
      if (newPeriod === 'Daily') {
        // Just today
        newStartDate = new Date(today);
        newEndDate = new Date(today);
      } else if (newPeriod === 'Weekly') {
        // Last 7 days (ending today)
        newStartDate.setDate(today.getDate() - 6); // Start 6 days ago to include today
        newEndDate = new Date(today);
      } else if (newPeriod === 'Monthly') {
        // Current month
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        // Calculate end of current month
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month
        // Clamp end date to today if it's in the future (the last day of the month hasn't happened yet)
        if (newEndDate > today) {
            newEndDate = new Date(today);
        }
      } else if (newPeriod === 'Quarterly') {
        // Current quarter
        const currentQuarter = Math.floor(today.getMonth() / 3);
        newStartDate = new Date(today.getFullYear(), currentQuarter * 3, 1); // First day of quarter
        // Calculate end of current quarter
        newEndDate = new Date(today.getFullYear(), currentQuarter * 3 + 3, 0); // Last day of quarter
        // Clamp end date to today if it's in the future (the last day of the quarter hasn't happened yet)
        if (newEndDate > today) {
            newEndDate = new Date(today);
        }
      }
      
      // Always update customDateRange regardless of the period type
      setCustomDateRange({
        startDate: newStartDate,
        endDate: newEndDate
      });
      
      // Reset the processing flag to allow data to be processed again with the new period
      hasProcessedRef.current = false;
      
      // Only call onPeriodChange if we're actually changing the period and it exists
      if (onPeriodChange) {
        // Call onPeriodChange directly instead of using setTimeout
        try {
          onPeriodChange(newPeriod);
        } catch (error) {
          console.error("Error updating period:", error);
        }
      }
    }
  }, [selectedPeriod, onPeriodChange]);

  const navigateToMediaClips = (filterType: 'sentiment' | 'category' | 'brand' | 'source', filterValue: string) => {
    const params = new URLSearchParams();
    
    // Preserve existing non-filter URL parameters (like 'period', etc.)
    if (typeof window !== 'undefined') {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        // Only add non-filter parameters initially
        if (!['brandName', 'category', 'sentiment', 'source', 'ids', 'startDate', 'endDate'].includes(key)) {
          params.set(key, value);
        }
      });
    }
    
    // Add all active dashboard filters, ensuring multiple values are comma-separated
    for (const [key, values] of Object.entries(activeDashboardFilters)) {
      if (values && values.length > 0) {
        params.set(key, values.join(','));
      }
    }
    
    // Map the clicked graph segment type to the correct URL parameter key
    const clickedParamKey = filterType === 'brand' ? 'brandName' : filterType;
    
    // Get the current values for the clicked filter type (if any)
    const existingValuesString = params.get(clickedParamKey);
    const existingValues = existingValuesString ? existingValuesString.split(',') : [];

    // Add the new filterValue only if it's not already present
    if (!existingValues.includes(filterValue)) {
      existingValues.push(filterValue);
      params.set(clickedParamKey, existingValues.join(','));
    }

    // Navigate to media clips page with the combined parameters
    if (typeof window !== 'undefined') {
      // Use router.push for potentially better history management
      router.push(`/media-clips?${params.toString()}`);
      // window.location.href = `/media-clips?${params.toString()}`; // Alternative if router has issues
    }
  }
  
  // Function to show the details modal
  const showDetailsModal = (
    filterType: 'sentiment' | 'category' | 'brand' | 'source', 
    filterValue: string,
    count: number = 0
  ) => {
    setSelectedFilterType(filterType);
    setSelectedFilterValue(filterValue);
    setSelectedCount(count);
    
    // Ensure we're capturing all relevant filter context for use in the modal
    // This will be used later when navigating via navigateToMediaClips
    
    // Log for debugging
    console.log(`Details modal opened: ${filterType}=${filterValue}, count=${count}, context:`, {
      selectedPeriod,
      selectedBrand,
      dashboardFilters,
      showExcludedCategories
    });
    
    setIsDetailsModalOpen(true);
  }
  
  // Define a helper function to get counts for a selected item
  const getCountForSelectedItem = (
    filterType: 'sentiment' | 'category' | 'brand' | 'source',
    filterValue: string
  ): number => {
    if (!filterType || !filterValue) return 0;
    
    switch (filterType) {
      case 'sentiment':
        return periodSentimentData.find(item => item.sentiment === filterValue)?.count || 0;
      case 'category':
        return categoryData.find(item => item.category === filterValue)?.count || 0;
      case 'brand':
        return brandCategories.find(item => item.category === filterValue)?.count || 0;
      case 'source':
        return sourceData.find(item => item.category === filterValue)?.count || 0;
      default:
        return 0;
    }
  }
  
  // This function returns a user-friendly name for the filter type
  const getFilterTypeName = (filterType: 'sentiment' | 'category' | 'brand' | 'source' | null): string => {
    if (!filterType) return '';
    
    switch (filterType) {
      case 'sentiment':
        return 'Sentiment';
      case 'category':
        return 'Category';
      case 'brand':
        return 'Brand';
      case 'source':
        return 'Source';
      default:
        return '';
    }
  }

  // Helper function to get the period label for a date
  const getPeriodLabel = (date: Date, periodType: PeriodType): string | null => {
    const now = new Date();
    
    if (periodType === 'Daily') {
      // Change from ISO format to MM/DD format to match groupClippingsByPeriod
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
    } else if (periodType === 'Weekly') {
      // Format as "Week X (MM/DD)" with the end date of the week
      const weekNumber = Math.ceil((date.getDate() + new Date(date.getFullYear(), date.getMonth(), 1).getDay()) / 7);
      
      // Calculate the end of the week (date + days remaining in the week)
      const endOfWeek = new Date(date);
      const daysToAdd = 6 - date.getDay(); // Assuming Sunday is 0, Saturday is 6
      endOfWeek.setDate(date.getDate() + daysToAdd);
      
      // Format the end date as MM/DD
      const endDateStr = endOfWeek.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
      
      return `Week ${weekNumber} (${endDateStr})`;
    } else if (periodType === 'Monthly') {
      // Format as "Mon YYYY"
      return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    } else if (periodType === 'Quarterly') {
      // Format as "Q1 YYYY"
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    }
    
    return null;
  };

  // Helper function to detect mobile browsers
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
    return mobileRegex.test(userAgent.toLowerCase());
  };

  // Fullscreen toggle function
  const toggleFullscreen = async () => {
    const element = graphContainerRef.current as any; // Use 'any' for broader prefix compatibility
    if (!element) return;

    // Check for various fullscreen element properties
    const isCurrentlyFullscreen = 
      document.fullscreenElement || 
      (document as any).webkitFullscreenElement || 
      (document as any).mozFullScreenElement || 
      (document as any).msFullscreenElement;
      
    const isMobile = isMobileDevice();
    console.log("toggleFullscreen called. isCurrentlyFullscreen:", !!isCurrentlyFullscreen, "isMobile:", isMobile, "User Agent:", navigator.userAgent);

    try {
      if (!isCurrentlyFullscreen) {
        console.log("Attempting to enter fullscreen...");
        // Try standard first, then vendor prefixes
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) { /* Safari */
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) { /* Firefox */
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) { /* IE/Edge */
          await element.msRequestFullscreen();
        }
        console.log("Entered fullscreen mode (state set).");
        setIsFullscreen(true);
      } else {
        console.log("Attempting to exit fullscreen...");
        // Try standard first, then vendor prefixes
        console.log("Checking document.exitFullscreen...");
        
        let exitSuccess = false;
        
        try {
          if (document.exitFullscreen) {
            console.log("Calling document.exitFullscreen()...");
            await document.exitFullscreen();
            console.log("Exited fullscreen via standard (state set).");
            exitSuccess = true;
          } else if ((document as any).webkitExitFullscreen) { /* Safari */
            console.log("Calling document.webkitExitFullscreen()...");
            await (document as any).webkitExitFullscreen();
            console.log("Exited fullscreen via webkit (state set).");
            exitSuccess = true;
          } else if ((document as any).mozCancelFullScreen) { /* Firefox */
            console.log("Calling document.mozCancelFullScreen()...");
            await (document as any).mozCancelFullScreen();
            console.log("Exited fullscreen via moz (state set).");
            exitSuccess = true;
          } else if ((document as any).msExitFullscreen) { /* IE/Edge */
            console.log("Calling document.msExitFullscreen()...");
            await (document as any).msExitFullscreen();
            console.log("Exited fullscreen via ms (state set).");
            exitSuccess = true;
          }
        } catch (exitError) {
          console.error("Error exiting fullscreen:", exitError);
          // Force the state to update anyway
          document.body.dispatchEvent(new Event('fullscreenchange'));
        }
        
        // Mobile fallback: If we couldn't exit fullscreen through standard methods,
        // force the UI to show as if we exited fullscreen
        if (!exitSuccess && isMobile) {
          console.log("Using mobile fallback for fullscreen exit");
          // Manually force the UI to exit fullscreen mode
          setIsFullscreen(false);
          
          // Dispatch synthetic events to make sure event listeners are triggered
          try {
            document.dispatchEvent(new Event('fullscreenchange'));
            document.dispatchEvent(new Event('webkitfullscreenchange'));
            document.dispatchEvent(new Event('mozfullscreenchange'));
            document.dispatchEvent(new Event('MSFullscreenChange'));
          } catch (e) {
            console.error("Failed to dispatch synthetic fullscreen change events:", e);
          }
        } else {
          // Always update the state in case the event listener doesn't fire
          setIsFullscreen(false);
        }
      }
    } catch (error) {
      console.error("Fullscreen request/exit failed:", error);
      // Attempt to sync state even if API call failed (e.g., user denied)
      const currentState = !!(
        document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).mozFullScreenElement || 
        (document as any).msFullscreenElement
      );
      
      // If we're on mobile and currently in fullscreen, force exit the UI state
      if (isMobile && isCurrentlyFullscreen) {
        console.log("Forcing UI out of fullscreen mode on mobile after error");
        setIsFullscreen(false);
      } else {
        setIsFullscreen(currentState);
      }
    }
  };

  // Effect to listen for fullscreen changes (e.g., pressing Esc)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const newFullscreenState = !!(
        document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).mozFullScreenElement || 
        (document as any).msFullscreenElement
      );
      console.log("Fullscreen change detected. New state:", newFullscreenState);
      setIsFullscreen(newFullscreenState);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange); // Safari
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);    // Firefox
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);     // IE/Edge

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  return (
    <div 
      ref={graphContainerRef} 
      className={cn(
        "h-full w-full flex flex-col", 
        isFullscreen ? "fixed inset-0 z-50 bg-white p-4 overflow-auto" : "" // Conditional fullscreen styles
      )}
    >
      {/* Mobile-specific fullscreen styles */}
      {isFullscreen && (
        <style jsx>{`
          @media (max-width: 640px) {
            /* Ensure controls are easily tappable on mobile */
            select, button {
              min-height: 36px !important; /* Ensure tap target size */
              padding: 8px !important; /* Consistent padding */
              font-size: 0.8rem !important; /* Slightly smaller font for controls */
            }
            
            /* Ensure the sticky header holding controls is visible */
            div[class*="sticky"] {
              display: flex !important; /* Make sure it's always flex */
              flex-wrap: wrap !important; /* Allow wrapping if controls overflow */
              align-items: center !important; /* Align items vertically */
              padding: 4px 8px !important; /* Adjust padding */
              z-index: 100 !important; /* Ensure it's above chart */
            }
            
            /* Style the controls container within the header */
            div[class*="sticky"] > div[class*="justify-between"] {
              width: 100% !important; /* Take full width */
              gap: 4px !important; /* Reduce gap between control groups */
            }

            /* Style the dropdown groups */
             div[class*="sticky"] div[class*="justify-start"] {
               flex-wrap: wrap; /* Allow dropdowns to wrap */
               gap: 4px !important;
               justify-content: space-around !important; /* Distribute space */
               flex-grow: 1; /* Allow group to grow */
             }

            /* Make sure the fullscreen button is prominent & fixed */
            div > button[aria-label="Exit fullscreen"] {
              position: absolute !important; /* Position relative to container */
              top: 8px !important;          /* Position from top */
              right: 8px !important;         /* Position from right */
              background-color: rgba(255, 255, 255, 0.8) !important; /* Semi-transparent background */
              border: 1px solid #ccc !important;
              border-radius: 50% !important;
              padding: 6px !important; /* Adjust padding */
              margin: 0 !important; /* Remove default margin */
              z-index: 110 !important; /* Ensure it's above header */
              box-shadow: 0 2px 4px rgba(0,0,0,0.2); /* Add shadow for visibility */
            }
            
            /* Fix any scrolling issues */
            :global(body) {
              overflow: hidden !important;
              position: fixed !important;
              width: 100% !important;
              height: 100% !important;
            }

            /* Adjust chart container padding */
            div[class*="flex-grow"] {
               padding-top: 50px !important; /* Add padding to avoid overlap with sticky header */
            }
          }

          /* Styles specifically for landscape mode on mobile */
          @media (max-width: 900px) and (orientation: landscape) {
             /* Ensure header doesn't take too much vertical space */
             div[class*="sticky"] {
               padding: 2px 8px !important;
               flex-wrap: nowrap !important; /* Prevent wrapping in landscape */
             }
             
             /* Reduce font size further if needed */
             select, button {
                font-size: 0.75rem !important;
             }
             
             /* Ensure chart takes full height */
             div[class*="flex-grow"] {
               padding-top: 40px !important; /* Adjust top padding for landscape header */
             }

             /* Position exit button slightly differently if needed */
             div > button[aria-label="Exit fullscreen"] {
                top: 5px !important;
                right: 5px !important;
                padding: 5px !important;
             }
          }
        `}</style>
      )}
      
      {/* Header and Controls - USE NEW COMPONENT */}
      <ActivityGraphControls
        chartType={chartType}
        breakdownType={breakdownType}
        selectedPeriod={selectedPeriod}
        isFullscreen={isFullscreen}
        onChartTypeChange={setChartType}
        onBreakdownTypeChange={setBreakdownType}
        onPeriodChange={handlePeriodChange} // Pass the existing handler
        onToggleFullscreen={toggleFullscreen}
        dashboardMode={dashboardMode}
      />
      
      {/* Main Chart Area */}
      <div className={cn(
        "flex-grow flex flex-col md:flex-row gap-2 mt-1 mb-0 pb-0 h-full",
        !isFullscreen ? "-mb-4" : "", // Adjust margin only when not fullscreen
        dashboardMode ? "mt-[-5px]" : "" // Reduce top margin in dashboard mode
      )}>
        {/* Activity Over Time Chart - Add legend-center class */} 
        <div className={cn(
          "w-full h-full flex-1 legend-center", 
          !dashboardFilters ? "md:w-full" : "md:w-2/3", // Take full width if not dashboard, otherwise 2/3
          isFullscreen ? "md:w-full" : "" // Force full width in fullscreen
          )}
        >
          <ChartRenderer 
            chartType={chartType}
            activityData={activityData}
            maxValue={maxValue}
            currentPeriod={getPeriodLabel(new Date(), selectedPeriod) || ''}
            brandCategories={brandCategories}
            categoryData={categoryData}
            sourceData={sourceData}
            breakdownType={breakdownType}
            categoriesByPeriod={categoriesByPeriodData}
            brandsByPeriod={brandsByPeriodData}
            sourcesByPeriod={sourcesByPeriodData}
            onNavigate={navigateToMediaClips}
          />
        </div>
        
        {/* Analysis Chart - Update text */} 
        {!dashboardFilters && (
          <div className={cn(
            "w-full md:w-1/3 h-auto md:h-full mt-4 md:mt-0",
            isFullscreen ? "hidden" : "" // Hide analysis chart in fullscreen mode
          )}>
            <h4 className="text-sm font-medium text-gray-600 mb-2 text-center">Analysis ({selectedPeriod})</h4>
            <CombinedPieChart 
              sentimentData={sentimentData} 
              categoryData={categoryData}
              brandData={brandCategories} // Use brandCategories directly
              sourceData={sourceData}
              activeChart={breakdownType}
              onSentimentClick={(sentiment: string) => navigateToMediaClips('sentiment', sentiment)}
              onCategoryClick={(category: string) => navigateToMediaClips('category', category)}
              onBrandClick={(brand: string) => navigateToMediaClips('brand', brand)}
              onSourceClick={(source: string) => navigateToMediaClips('source', source)}
            />
          </div>
        )}
      </div>

      {/* Details Modal - Update period display */}
      {isDetailsModalOpen && selectedFilterType && selectedFilterValue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {getFilterTypeName(selectedFilterType)}: {selectedFilterValue}
              </h3>
              <button 
                onClick={() => setIsDetailsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Period:</span> {selectedPeriod}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Total Media Clips:</span> {selectedCount}
                </p>
                {selectedBrand && selectedBrand !== 'All Brands' && (
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Brand:</span> {selectedBrand}
                  </p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    if (selectedFilterType && selectedFilterValue) {
                      console.log('Navigating from modal with:', {
                        filterType: selectedFilterType,
                        filterValue: selectedFilterValue,
                        selectedBrand,
                        selectedPeriod
                      });
                      navigateToMediaClips(selectedFilterType, selectedFilterValue);
                    }
                  }}
                  className="px-4 py-2 bg-[#8A6FE8] text-white rounded-md hover:bg-[#7A5FD8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8A6FE8]"
                >
                  View Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 